import { GraphQLClient } from "graphql-request";
import * as gql from "gql-query-builder";
import {
  DataProvider,
  BaseRecord,
  GetListResponse,
  CustomResponse,
  CustomParams,
  GetListParams,
  GetOneParams,
  CreateParams,
  UpdateParams,
  DeleteOneParams,
  DeleteOneResponse,
} from "@refinedev/core";
import { envService } from "../config/env";
import { authService } from "../services/authService";
import { calculateOperationName, calculateInputType } from "./utils/graphqlUtils";
import { tenantService } from "../services/tenantService";

type ClientBuilder = (url: string) => GraphQLClient;

const defaultClientBuilder = (url: string): GraphQLClient => {
  return new GraphQLClient(url, {
    credentials: "include",
  });
};

export const dataProvider = (
  url: string,
  clientBuilder: ClientBuilder = defaultClientBuilder
): DataProvider & Required<Pick<DataProvider, "custom">> => {
  const client = clientBuilder(url);

  return {
    getList: async <TData extends BaseRecord = BaseRecord>({
      resource,
      pagination,
      filters,
      sorters,
      meta,
    }: GetListParams): Promise<GetListResponse<TData>> => {
      const { current = 1, pageSize = 10, mode = "server" } = pagination ?? {};
      const operation = meta?.operation ?? calculateOperationName(resource, "getList");
      const fields = meta?.fields ?? ["id"];
      const paginationFields = [{ edges: fields }, { pageInfo: ["page", "size"] }, "totalCount", "totalPages"];

      const paginationVariables =
        mode === "server"
          ? {
              page: {
                value: current,
                type: "Int",
                required: false,
              },
              size: {
                value: pageSize,
                type: "Int",
                required: false,
              },
            }
          : {};

      const filterVariables =
        meta?.filterMode === "off"
          ? {}
          : {
              input: {
                value: filters?.length === 0 ? {} : filters,
                type: calculateInputType(resource, "where", { required: true }),
                required: false,
              },
            };

      const sortVariables =
        meta?.sortMode === "off" || !sorters
          ? {}
          : {
              orderBy: {
                value: sorters?.map((sorter) => ({
                  field: sorter.field.toUpperCase(),
                  direction: sorter.order === "asc" ? "ASC" : "DESC",
                })),
                type: `[${calculateInputType(resource, "order")}]`,
                required: false,
              },
            };

      const query = gql.query({
        operation,
        variables: {
          ...paginationVariables,
          ...filterVariables,
          ...sortVariables,
        },
        fields: mode === "server" ? paginationFields : fields,
      });

      const response = await client.request<TData>(query.query, query.variables);
      if (mode === "server") {
        return {
          data: response[operation].edges,
          total: response[operation].totalCount,
        };
      }

      return {
        data: response[operation],
        total: response[operation].length,
      };
    },

    getOne: async <TData extends BaseRecord = BaseRecord>({
      resource,
      id,
      meta,
    }: GetOneParams): Promise<{ data: TData }> => {
      const operation = meta?.operation ?? calculateOperationName(resource, "getOne");

      const query = gql.query({
        operation,
        variables: {
          id: {
            value: id,
            type: "ID!",
          },
        },
        fields: meta?.fields || ["id"],
      });

      const response = await client.request<TData>(query.query, query.variables);

      return { data: response[operation] };
    },

    create: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, never>>({
      resource,
      variables,
      meta,
    }: CreateParams<TVariables>): Promise<{ data: TData }> => {
      const operation = meta?.operation ?? calculateOperationName(resource, "create");

      const query = gql.mutation({
        operation,
        variables: {
          input: {
            value: variables,
            type: meta?.inputType ?? calculateInputType(resource, "create", { required: true, position: "prefix" }),
          },
        },
        fields: meta?.fields || ["id"],
      });

      const response = await client.request<TData>(query.query, query.variables);

      return { data: response[operation] };
    },

    update: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, never>>({
      resource,
      id,
      variables,
      meta,
    }: UpdateParams<TVariables>): Promise<{ data: TData }> => {
      const operation = meta?.operation ?? calculateOperationName(resource, "update");

      const query = gql.mutation({
        operation,
        variables: {
          id: {
            value: id,
            type: "ID!",
          },
          input: {
            value: variables,
            type: meta?.inputType ?? calculateInputType(resource, "update", { required: true }),
          },
        },
        fields: meta?.fields,
      });

      const response = await client.request<TData>(query.query, query.variables);

      return { data: response[operation] };
    },

    deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = Record<string, never>>({
      resource,
      id,
      meta,
    }: DeleteOneParams<TVariables>): Promise<DeleteOneResponse<TData>> => {
      const operation = meta?.operation ?? calculateOperationName(resource, "delete");

      const query = gql.mutation({
        operation,
        variables: {
          id: {
            value: id,
            type: "ID!",
          },
        },
        fields: meta?.fields,
      });

      const response = await client.request<TData>(query.query, query.variables);

      return { data: response[operation] };
    },

    getApiUrl: () => {
      return url;
    },

    custom: async <TData extends BaseRecord = BaseRecord>({
      meta,
      headers,
    }: CustomParams): Promise<CustomResponse<TData>> => {
      const customOperation = meta?.operation ?? meta?.gqlMutation;

      if (customOperation) {
        const response = await client.request<TData>(customOperation, meta?.variables, headers);
        return { data: response };
      }

      throw new Error("Custom query is required for custom method");
    },
  };
};

export const gmbhDataProvider = dataProvider(envService.getValue("VITE_GMBH_BACKEND_URL"), (url) => {
  return new GraphQLClient(url, {
    credentials: "include",
    requestMiddleware: (request) => {
      request.headers = {
        ...request.headers,
        "X-Tenant-ID": tenantService.getCurrentTenant().id,
      };
      return request;
    },
  });
});

export const ggmbhDataProvider = dataProvider(envService.getValue("VITE_GGMBH_BACKEND_URL"), (url) => {
  return new GraphQLClient(url, {
    credentials: "include",
    errorPolicy: "all",
    requestMiddleware: async (request) => {
      if (!authService.isAuthenticated()) {
        await authService.fetchAccessToken();
      }

      const token = authService.getToken();
      const currentTenant = tenantService.getCurrentTenant();

      if (token) {
        request.headers = {
          ...request.headers,
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": currentTenant.id,
        };
      }

      return request;
    },
    responseMiddleware: (response) => {
      if (response instanceof Error) {
        console.error("GraphQL Error:", response);
      }
      return response;
    },
  });
});
